import { Builder } from 'builder-pattern';
import { v4 as uuid } from 'uuid';
import {
  ProductRoomChoice,
  ProductRoomRate,
  ProductRoomRatePricing,
  ProductRoomSelection,
  ProductStayRooms,
  StayDetailsResponse,
  ProductStayTrip,
} from '../../src/types/StayDetailsResponse';

const pricingTemplate = {
  currency: 'AUD',
  price: 674.69,
  taxes: 0,
  total: 674.69,
};
const roomRateTemplate = {
  rateId: '256039be-d013-473d-91d1-2ae508bb433d',
  additionalInfo: {
    nonRefundable: true,
    refundableDescription: '',
  },
  mealPlan: 'Room only RO',
  promotions: [],
  ancillaries: [],
  pricing: pricingTemplate,
  choiceKey: 'HTL__0__GIM4012827__Superior room, 2 double beds~2~0~0~1~Room only RO_RF__11',
};

// ###############################################
// ############### CHOICES 1 RATES ###############
// ###############################################

const choicesOneRateOne = Builder<ProductRoomRate>(roomRateTemplate)
  .choiceKey('HTL__0__GIM4012827__Superior room, 2 double beds~2~0~0~1~Room only RO_RF__11')
  .build();
const choicesOneRateTwo = Builder<ProductRoomRate>(roomRateTemplate)
  .rateId('e5917cda-5d54-4b56-a1f3-d48ac15d8bdc')
  .additionalInfo({
    nonRefundable: false,
    refundableDescription: '2023-06-12T23:59:00',
  })
  .pricing(Builder<ProductRoomRatePricing>(pricingTemplate).price(763.63).total(763.63).build())
  .choiceKey('HTL__0__GIM4012827__Superior triple, 3 single beds~2~0~0~1~Room only RO_RF__11')
  .build();
const choicesOneRateThree = Builder<ProductRoomRate>(roomRateTemplate)
  .rateId('a21879f5-4aa2-4a04-b60b-701e1d6762f1')
  .additionalInfo({
    nonRefundable: false,
    refundableDescription: '2023-06-12T23:59:00',
  })
  .mealPlan('Bed and Breakfast BB')
  .pricing(Builder<ProductRoomRatePricing>(pricingTemplate).price(852.55).total(852.55).build())
  .choiceKey('HTL__0__GIM4005613__Room with river view-WBBDN~2~0~0~1~Bed and Breakfast BB_RF__11')
  .build();

// ###############################################
// ############### CHOICES 2 RATES ###############
// ###############################################

const choicesTwoRateOne = Builder<ProductRoomRate>(roomRateTemplate)
  .rateId('f28b6489-d9fb-4764-b7b0-91298ccd17c7')
  .pricing(Builder<ProductRoomRatePricing>(pricingTemplate).price(765).total(765).build())
  .choiceKey('HTL__0__GIM4005613__1 bedroom standard apartment-W3NDN~2~0~0~1~Room only RO_NR__11')
  .build();
const choicesTwoRateTwo = Builder<ProductRoomRate>(roomRateTemplate)
  .rateId('9a9da0e9-d444-499b-9a9a-245753b5ce8d')
  .additionalInfo({
    nonRefundable: false,
    refundableDescription: '2023-06-12T23:59:00',
  })
  .pricing(Builder<ProductRoomRatePricing>(pricingTemplate).price(866.13).total(866.13).build())
  .choiceKey('HTL__0__GIM4005613__1 bedroom standard apartment-WBFDN~2~0~0~1~Room only RO_RF__11')
  .build();
const choicesTwoRateThree = Builder<ProductRoomRate>(roomRateTemplate)
  .rateId('d33d05cb-6914-4d71-83fc-1ce140e85abf')
  .mealPlan('Bed and Breakfast BB')
  .additionalInfo({
    nonRefundable: false,
    refundableDescription: '2023-06-12T23:59:00',
  })
  .pricing(Builder<ProductRoomRatePricing>(pricingTemplate).price(1071.13).total(1071.13).build())
  .choiceKey('HTL__0__GIM4005613__1 bedroom standard apartment-WBBDN~2~0~0~1~Bed and Breakfast BB_RF__11')
  .build();

// ###############################################
// ############### CHOICES #######################
// ###############################################

const choiceTemplate = {
  roomId: 'f9bfaad2-76a2-44f5-b799-29ac81d6b895',
  typeName: 'Room with river view  (2,0,0)',
  bedding: 'Twin Bed',
  rates: [choicesOneRateOne, choicesOneRateTwo, choicesOneRateThree],
  images: ['https://az712897.vo.msecnd.net/images/full/694f9c2b-fcc6-46b6-94f9-efa0332c0c93.jpeg'],
  description:
    '\noverview : <p><strong>2 Twin Beds</strong></p><p>248 sq feet </p><br/><p><b>Internet</b> - Free WiFi </p><p><b>Entertainment</b> - 43-inch flat-screen TV with digital channels</p><p><b>Food & Drink</b> - Refrigerator and coffee/tea maker </p><p><b>Sleep</b> - Bed sheets </p><p><b>Bathroom</b> - Private bathroom, shower/tub combination, free toiletries, and a hair dryer</p><p><b>Practical</b> - Safe, iron/ironing board, and laptop workspace; free cribs/infant beds available on request</p><p><b>Comfort</b> - Climate-controlled air conditioning and daily housekeeping</p><p>Non-Smoking</p>',
};

const choicesOne = Builder<ProductRoomChoice>(choiceTemplate)
  .inclusions(
    Builder<ProductRoomChoice['inclusions']>()
      .bedding('Twin Bed')
      .nonSmoking('Non-smoking')
      .roomSize('50sqm')
      .view(undefined)
      .build(),
  )
  .amenities([
    {
      title: 'Internet',
      values: ['Free WiFi'],
    },
    {
      title: 'Entertainment',
      values: ['40-inch TV'],
    },
    {
      title: 'Food & Drink',
      values: ['Refrigerator, microwave,  shared/communal kitchen access, and toaster'],
    },
    {
      title: 'Sleep',
      values: ['Bed sheets'],
    },
    {
      title: 'Bathroom',
      values: ['Shared bathroom'],
    },
    {
      title: 'Practical',
      values: ['Guidebooks'],
    },
    {
      title: 'Comfort',
      values: ['Air conditioning, daily housekeeping, and heating'],
    },
    {
      title: 'Need to Know',
      values: ['No cribs (infant beds) or rollaway/extra beds available', 'Non-Smoking'],
    },
  ])
  .build();
const choicesTwo = Builder<ProductRoomChoice>(choiceTemplate)
  .roomId('2eeb8277-9219-414a-8634-99333db6de65')
  .typeName('1 bedroom standard apartment  (2,0,0)')
  .bedding('Queen Bed')
  .rates([choicesTwoRateOne, choicesTwoRateTwo, choicesTwoRateThree])
  .images(['https://az712897.vo.msecnd.net/images/full/694f9c2b-fcc6-46b6-94f9-efa0332c0c93.jpeg'])
  .description(
    '\noverview : <p><strong>1 Queen Bed</strong></p><p>248 sq feet </p><br/><p><b>Internet</b> - Free WiFi </p><p><b>Entertainment</b> - 43-inch flat-screen TV with digital channels</p><p><b>Food & Drink</b> - Refrigerator and coffee/tea maker </p><p><b>Sleep</b> - Bed sheets </p><p><b>Bathroom</b> - Private bathroom, shower/tub combination, free toiletries, and a hair dryer</p><p><b>Practical</b> - Safe, iron/ironing board, and laptop workspace</p><p><b>Comfort</b> - Climate-controlled air conditioning and daily housekeeping</p><p>Non-Smoking</p>',
  )
  .amenities([
    {
      title: 'Internet',
      values: ['Free WiFi'],
    },
    {
      title: 'Entertainment',
      values: ['40-inch TV'],
    },
    {
      title: 'Food & Drink',
      values: ['Refrigerator, microwave,  shared/communal kitchen access, and toaster'],
    },
    {
      title: 'Sleep',
      values: ['Bed sheets'],
    },
    {
      title: 'Bathroom',
      values: ['Shared bathroom'],
    },
    {
      title: 'Practical',
      values: ['Guidebooks'],
    },
    {
      title: 'Comfort',
      values: ['Air conditioning, daily housekeeping, and heating'],
    },
    {
      title: 'Need to Know',
      values: ['No cribs (infant beds) or rollaway/extra beds available', 'Non-Smoking'],
    },
  ])
  .inclusions(
    Builder<ProductRoomChoice['inclusions']>()
      .bedding('Queen Bed')
      .nonSmoking('Non-smoking')
      .roomSize('304sqm')
      .view(undefined)
      .build(),
  )
  .build();
// ###############################################
// ############### PAYLOAD #######################
// ###############################################

const ParkRegisNorthQuayOffering = ({ stayId = '4277677', offerId = uuid() }): StayDetailsResponse =>
  Builder<StayDetailsResponse>()
    .offeringId(offerId)
    .productType('STAY')
    .productId('HTL~1')
    .travellerIds(['1', '2'])
    .stayId(stayId)
    .stay(
      Builder<StayDetailsResponse['stay']>()
        .name('Park Regis North Quay')
        .description(
          "Metro Hotel Marlow Sydney Central is located in Sydney CBD (Central Business District) close to public transport and just 5 minutes’ walk from Central Station. The famous Capitol Theater is located opposite the hotel. Each of the spacious and comfortable rooms includes air conditioning and tea/coffee making facilities. Free WiFi is available in all rooms. Laundry and dry cleaning services are available for guests. Reception is open 24 hours a day. Metro Hotel Marlow Sydney Central is located on the edge of Chinatown less than 10 minutes’ walk from Darling Harbor and Sydney's main shopping district. Many cafes and restaurants are on the doorstep.",
        )
        .build(),
    )
    .trip(
      Builder<ProductStayTrip>()
        .startDate('2023-06-13')
        .endDate('2023-06-18')
        .conditions([])
        .duration(Builder<ProductStayTrip['duration']>().unit('NIGHTS').value(5).build())
        .build(),
    )
    .rooms(
      Builder<ProductStayRooms>()
        .selected([
          Builder<ProductRoomSelection>()
            .choiceKey('HTL__0__GIM4005613__Room with river view-WBBDN~2~0~0~1~Bed and Breakfast BB_RF__11')
            .roomId('f9bfaad2-76a2-44f5-b799-29ac81d6b895')
            .rateId('a21879f5-4aa2-4a04-b60b-701e1d6762f1')
            .travellerIds(['1', '2'])
            .ancillaries([])
            .build(),
        ])
        .choices([choicesOne, choicesTwo])
        .build(),
    )
    // .families() DEPRECATED
    .cancellationRules([])
    .summary({ pricing: Builder<ProductRoomRatePricing>(pricingTemplate).price(674.69).total(674.69).build() })
    .bookableOnline(true)
    .build();
export const TwinBedRoomChoice = (): ProductRoomChoice => choicesOne;

export default ParkRegisNorthQuayOffering;
